// Block-based configurable messages (CPK-3787).
//
// Admins author CONTENT + STRUCTURE only (which blocks, what text, which named
// variant). Pixel-level styling (colours, spacing, fonts) lives in the block
// sub-components + theme tokens — never in the DB. Keeps every configured
// message on-theme, responsive, i18n-friendly and XSS-free (text is rendered
// via interpolation / sanitized markdown; no raw HTML is stored).
//
// Stored as JSONB wherever a message is configured and rendered by
// <app-message-renderer [doc]="...">.

export interface LocalizedText {
  en: string;
  pt: string;
}

/** Semantic colour intent — mapped to theme tokens in the SCSS, NOT raw hex. */
export type BlockVariant = 'brand' | 'info' | 'success' | 'warning' | 'danger';

export type TextAlign = 'left' | 'center';

/** Shared fields available on every block. */
export interface BlockBase {
  /** When true, the block is kept in the doc but not rendered (toggle visibility). */
  hidden?: boolean;
}

/** Circular icon badge (e.g. the clock on the "under review" screen). */
export interface IconBadgeBlock extends BlockBase {
  type: 'iconBadge';
  /** Material icon ligature name, e.g. 'schedule', 'search', 'mail', 'draw'. */
  icon: string;
  variant?: BlockVariant;
}

export interface HeadingBlock extends BlockBase {
  type: 'heading';
  text: LocalizedText;
  level?: 'h1' | 'h2';
  align?: TextAlign;
}

/** Body text. Inline markdown (bold, links, lists) is supported and sanitized. */
export interface ParagraphBlock extends BlockBase {
  type: 'paragraph';
  text: LocalizedText;
  align?: TextAlign;
}

export interface MessageCard {
  /** Material icon ligature name. Optional. */
  icon?: string;
  /** Icon colour intent (same semantics as iconBadge); omit for neutral. */
  variant?: BlockVariant;
  title: LocalizedText;
  body: LocalizedText;
}

/** A stack of info cards (icon + title + body). */
export interface CardGroupBlock extends BlockBase {
  type: 'cardGroup';
  cards: MessageCard[];
}

/** A highlighted note/callout. `variant` picks the semantic colour. */
export interface NoteBlock extends BlockBase {
  type: 'note';
  text: LocalizedText;
  variant?: BlockVariant;
}

export interface ButtonBlock extends BlockBase {
  type: 'button';
  label: LocalizedText;
  /** Opaque action id the host component interprets (e.g. 'proceed'). */
  action?: string;
  variant?: 'primary' | 'secondary';
}

/** Progress dots, e.g. "Etapa 3 de 5". */
export interface StepperBlock extends BlockBase {
  type: 'stepper';
  total: number;
  /** 1-based index of the active step. */
  current: number;
  /** Optional caption above the dots (e.g. "Etapa 3 de 5"). */
  caption?: LocalizedText;
  variant?: BlockVariant;
}

export interface ListItem {
  /** Material icon ligature; defaults to a check mark. */
  icon?: string;
  variant?: BlockVariant;
  /** Item text (inline markdown supported). */
  text: LocalizedText;
}

/** A vertical list of icon + text rows (checklist, bullet list, steps). */
export interface ListBlock extends BlockBase {
  type: 'list';
  items: ListItem[];
}

export type MessageBlock =
  | IconBadgeBlock
  | HeadingBlock
  | ParagraphBlock
  | CardGroupBlock
  | NoteBlock
  | ButtonBlock
  | StepperBlock
  | ListBlock;

export interface MessageDocument {
  /** Schema version, for forward-compatible migrations. */
  version?: 1;
  blocks: MessageBlock[];
}

export type BlockLang = 'en' | 'pt';

/** Resolve a LocalizedText to a plain string in the given language. */
export function localizedText(value: LocalizedText | undefined, lang: BlockLang): string {
  if (!value) return '';
  return value[lang] || value.en || '';
}

/** Narrow an unknown value (e.g. a raw JSONB column) to a MessageDocument, or null. */
export function asMessageDocument(value: unknown): MessageDocument | null {
  if (
    value != null &&
    typeof value === 'object' &&
    Array.isArray((value as MessageDocument).blocks)
  ) {
    return value as MessageDocument;
  }
  return null;
}
